import { BackInStockReminderService, IBackInStockNotificationData } from '@Api/BackInStockReminderService';
import { EngagementProvider } from '@Base/Engagement/Engagement';
import { Fancyapps } from '@Libraries/Fancyapps';
import { Form } from '@Utilities/Form';
import { TextResource } from '@Utilities/TextResource';
import { AxiosError, AxiosResponse } from 'axios';
import isEmail from "validator/es/lib/isEmail";

export interface IBackInStockReminder
{
	ApiData: IBackInStockNotificationData;
	CheckedItems: NodeListOf<HTMLInputElement>;
	Email: string;
	Faux: string;
	ProductID: string;
	ProductSizeID: string;
	ProductSkus: string;
}

export class BackInStock
{
	private Fancyapps: Fancyapps;
	private Form: Form;
	private FormEl: HTMLFormElement;
	private Launchers: NodeListOf<HTMLButtonElement>;
	private Reminder: IBackInStockReminder;

	constructor()
	{
		this.BindForm();
		this.BindLaunchers();
	}

	private BindForm = (): void =>
	{
		this.FormEl = document.getElementById("frmStockReminder") as HTMLFormElement;

		if (this.FormEl !== null)
		{
			this.Form = new Form(this.FormEl);
			this.Form.ShowErrorHeader = false;
			this.Form.SubmitHandler = this.SubmitReminder;
			this.Form.ValidationHandler = this.ValidateReminder;
		}
	};

	private BindLaunchers = (): void =>
	{
		this.Launchers = document.querySelectorAll(".oosLink") as NodeListOf<HTMLButtonElement>;

		if (this.Launchers.length > 0)
		{
			this.Launchers.forEach((launcher: HTMLButtonElement) =>
			{
				launcher.addEventListener("click", this.ImportModalScript);
			});

			if (this.Launchers.length === 1)
			{
				const singleSizeOosLink = document.querySelector("#hlOutOfStock") as HTMLLinkElement;

				if (singleSizeOosLink !== null)
				{
					singleSizeOosLink.addEventListener("click", this.SingleSizeLinkHandler);
				}
			}
		}
	};

	private ConfigureFormSelections = (e: Event): void =>
	{
		const target = e.currentTarget as HTMLButtonElement;
		const parent = target.parentElement as HTMLFormElement;
		const size = parent.getAttribute("data-size");
		const inputs = this.FormEl.querySelectorAll("input[name='Size']");
		const selectedSize = this.FormEl.querySelector(`input[value="${size}"]`) as HTMLInputElement;

		inputs.forEach((input: HTMLInputElement): void =>
		{
			input.checked = false;
		});

		selectedSize.checked = true;
	};

	private ConfigureModal = async (): Promise<void> =>
	{
		this.Fancyapps = await Fancyapps.Import();
		this.Fancyapps.Fancybox.bind("[data-fancybox='BackInStock']", {
			dragToClose: false,
			groupAttr: false
		});
	};

	private GetReminder = (): IBackInStockReminder =>
	{
		const checkedItems = this.Form.Element.querySelectorAll("input:checked") as NodeListOf<HTMLInputElement>;
		const checked: string[] = [];
		const checkedSkus: string[] = [];
		let email: string;
		const emailEl = this.Form.Element.querySelector("input[name='Alternate']") as HTMLInputElement;
		let faux: string;
		const fauxEl: HTMLInputElement = this.Form.Element.querySelector("input[name='Email']");
		let productID: string;
		const productIDEl: HTMLInputElement = this.Form.Element.querySelector("input[name='ProductID']");
		let productSizeID: string;
		let productSkus: string;

		if (checkedItems.length > 0)
		{
			checkedItems.forEach((checkedItem) =>
			{
				checked.push(checkedItem.value);
				checkedSkus.push(checkedItem.getAttribute("data-sku"));
			});

			productSizeID = checked.join(",");
			productSkus = checkedSkus.join(",");
		}

		if (fauxEl !== null)
		{
			faux = fauxEl.value;
		}

		if (emailEl !== null)
		{
			email = emailEl.value;
		}

		if (productIDEl !== null)
		{
			productID = productIDEl.value;
		}

		return this.Reminder = {
			ApiData: {
				email: email,
				productID: productID,
				sizeList: productSizeID,
				skuList: productSkus
			},
			CheckedItems: checkedItems,
			Email: email,
			Faux: faux,
			ProductID: productID,
			ProductSizeID: productSizeID,
			ProductSkus: productSkus
		};
	};

	private ImportModalScript = (e: Event): void =>
	{
		e.preventDefault();
		const target = e.currentTarget as HTMLButtonElement;
		this.UnbindLaunchers();
		this.ConfigureModal()
			.then(() =>
			{
				target.click();
			})
			.catch((error) =>
			{
				console.log(error);
			});
	};

	private SingleSizeLinkHandler = (e: Event): void =>
	{
		e.preventDefault();

		if (this.Launchers.length > 0)
		{
			this.Launchers[0].click();
		}
	};

	private SubmitReminder = (): void =>
	{
		const reminderService = new BackInStockReminderService();
		const engagementProvider = new EngagementProvider();

		if (this.Reminder.Faux == null || this.Reminder.Faux === undefined || this.Reminder.Faux === "")
		{
			engagementProvider.CreateGuestAccount(this.Reminder.Email)
				.then(() =>
				{
					reminderService.AddReminder(this.Reminder.ApiData)
						.then((response: AxiosResponse) =>
						{
							const data = response.data;
							if (data.Error && data.Error.length > 0)
							{
								data.Error.foreach((error) =>
								{
									this.Form.Errors.push(error);
								});
								this.Form.ListErrors();
							}
							else
							{
								if (data.d)
								{
									this.Form.Errors.push(TextResource.GetTextByCulture("Feature_Theme_NotiErr"));
									this.Form.ListErrors();
								}
								else
								{
									this.Form.DisplaySuccess(TextResource.GetTextByCulture("Feature_Theme_SgnUpThnkYou"));
								}
							}
						})
						.catch((error: AxiosError) =>
						{
							console.log(error);
						});
				});
		}
		else
		{
			const spammer = new this.Fancyapps.Fancybox([
				{
					src: TextResource.GetTextByCulture("Feature_Theme_EmailThankYou"),
					type: "html"
				}
			]);
		}
	};

	private UnbindLaunchers = (): void =>
	{
		if (this.Launchers.length > 0)
		{
			this.Launchers.forEach((launcher: HTMLButtonElement) =>
			{
				launcher.removeEventListener("click", this.ImportModalScript);
				launcher.addEventListener("click", this.ConfigureFormSelections);
			});
		}
	};

	private ValidateReminder = (): boolean =>
	{
		this.GetReminder();

		if (this.Reminder.Faux === null || this.Reminder.Faux === undefined || this.Reminder.Faux === "")
		{
			if (this.Reminder.CheckedItems.length < 1)
			{
				this.Form.Errors.push(TextResource.GetTextByCulture("Feature_Theme_SelectSize"));
			}

			if (!isEmail(this.Reminder.Email))
			{
				this.Form.Errors.push(TextResource.GetTextByCulture("Feature_Shipping_ValidEmail"));
			}

			if (this.Form.Errors.length < 1)
			{
				return true;
			}
			else
			{
				return false;
			}
		}
		else
		{
			return false;
		}
	};
}